/**
 * Ratentabelle der Liquiditaetsplanung: alle Plan-Raten (fix, geplant, offen), sortierbar per
 * Spaltenkopf, mit Spaltenfiltern aus `components/Board/filter/` (Mehrfachauswahl, Datums- und
 * Betragsbereich), Filterchips und Summenzeile.
 *
 * Der `Spaltenkopf` des Bestellboards ist an `Order`/`orderList.ts` gebunden und hier nicht
 * nutzbar; das Menue (`details`/`summary`, `.order-list-filter-menu`) ist seine schmale Nachbildung
 * mit denselben Widgets. Es ist immer hoechstens ein Menue offen.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Ikone } from "../Ikone.tsx";
import { istEinfacherKlick, Verweis } from "../Verweis.tsx";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { kennungVon } from "../../lib/bestellkennung.ts";
import { fmtDate, fmtUsd } from "../../lib/format.ts";
import {
  sortiereRaten,
  STANDARD_RATEN_SORTIERUNG,
  transportartLabel,
  type LiquiditaetsRate,
  type RatenSortierung,
  type RatenSpalte,
} from "../../lib/liquiditaet.ts";
import type { Bereichsfilter, OrderListFacet } from "../../lib/orderList.ts";
import { BetragsBereichFilter } from "../Board/filter/BetragsBereichFilter.tsx";
import { DatumsBereichFilter } from "../Board/filter/DatumsBereichFilter.tsx";
import { MehrfachauswahlFilter } from "../Board/filter/MehrfachauswahlFilter.tsx";
import type { DrawerTabId } from "../Drawer/drawerRechte.ts";
import { filterAktiv, LEERER_FILTER, STATUS_LABEL, type FilterChip, type RatenFilter } from "./filter.ts";

// Die Reihenfolge der Tabelle (`sortiereRaten`, die Spalten, die Vorgabe) steht seit dem KI-Zugang (Stufe 1) in `lib/liquiditaet.ts`:
// der Server nennt die Raten in derselben Reihenfolge. Hier bleiben die Namen der Komponente.
type SpaltenSchluessel = RatenSpalte;
type Sortierung = RatenSortierung;

const optionen = (werte: readonly (readonly [string, string])[]): OrderListFacet[] =>
  werte.map(([value, label]) => ({ value, label }));

export interface RatenTabelleProps {
  /** Alle Plan-Raten (Quelle der Filteroptionen). */
  alle: readonly LiquiditaetsRate[];
  /** Nach Kachel, Woche und Spalten gefilterte Raten. */
  gefiltert: readonly LiquiditaetsRate[];
  filter: RatenFilter;
  onFilter: (neu: RatenFilter) => void;
  chips: readonly FilterChip[];
  onOeffnen: (bestellungId: string, tab?: DrawerTabId) => void;
}

export function RatenTabelle({ alle, gefiltert, filter, onFilter, chips, onOeffnen }: RatenTabelleProps) {
  const { ansicht } = useAnsicht();
  const [sortierung, setSortierung] = useState<Sortierung>(STANDARD_RATEN_SORTIERUNG);
  const [offen, setOffen] = useState<SpaltenSchluessel | null>(null);
  const zeilen = useMemo(() => sortiereRaten(gefiltert, sortierung), [gefiltert, sortierung]);
  const summe = gefiltert.reduce((s, r) => s + r.usd, 0);
  const tabelle = useRef<HTMLTableElement>(null);

  // Klick ausserhalb schliesst das offene Menue (Capture-Phase, wie `Spaltenkopf.tsx`: Zeilen
  // koennen die Ausbreitung stoppen).
  useEffect(() => {
    if (!offen) return;
    const aussen = (ereignis: MouseEvent): void => {
      const menue = tabelle.current?.querySelector(`th[data-liq-spalte="${offen}"] details`);
      if (menue?.contains(ereignis.target as Node)) return;
      setOffen(null);
    };
    document.addEventListener("click", aussen, true);
    return () => document.removeEventListener("click", aussen, true);
  }, [offen]);

  const lieferantenOptionen = useMemo(
    () =>
      optionen(
        [...new Set(alle.map((r) => r.lieferant))]
          .sort((a, b) => a.localeCompare(b, "de"))
          .map((l) => [l, l || "(ohne Lieferant)"] as const),
      ),
    [alle],
  );

  const sortieren = (key: SpaltenSchluessel): void =>
    setSortierung((alt) => (alt.key === key ? { key, dir: alt.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const kopf = (key: SpaltenSchluessel, label: string, widget: ReactNode | null, aktiv: boolean, num = false) => {
    const sortAktiv = sortierung.key === key;
    const icon = sortAktiv ? (sortierung.dir === "asc" ? "arrow-up" : "arrow-down") : "arrow-up-down";
    return (
      <th
        data-liq-spalte={key}
        className={num ? "num" : undefined}
        aria-sort={sortAktiv ? (sortierung.dir === "asc" ? "ascending" : "descending") : "none"}
      >
        <div className="order-list-th liq-th">
          <button className="order-list-sort-btn" type="button" onClick={() => sortieren(key)} title={`Nach ${label} sortieren`}>
            <span>{label}</span>
            <span className="order-list-sort"><Ikone name={icon} /></span>
          </button>
          {widget ? (
            <details
              className={`order-list-filter ${aktiv ? "active" : ""}`}
              open={offen === key}
              onClick={(e) => e.stopPropagation()}
              onToggle={(e) => {
                const istOffen = (e.target as HTMLDetailsElement).open;
                setOffen((alt) => (istOffen ? key : alt === key ? null : alt));
              }}
            >
              <summary title={`${label} filtern`} aria-label={`${label} filtern`}><Ikone name="filter" /></summary>
              <div className="order-list-filter-menu" hidden={offen !== key}>
                <button type="button" className="spaltenfilter-schliessen" aria-label={`${label}-Filter schließen`} onClick={() => setOffen(null)}>
                  <Ikone name="x" />
                </button>
                <label>{`${label} filtern`}</label>
                {widget}
              </div>
            </details>
          ) : null}
        </div>
      </th>
    );
  };

  const set = <K extends keyof RatenFilter>(k: K, v: RatenFilter[K]): void => onFilter({ ...filter, [k]: v });

  return (
    <div className="ov-section liq-tabelle-bereich">
      <div className="ov-section-head">
        <h2>Raten</h2>
        <span className="cost-count">{`${gefiltert.length} von ${alle.length} Raten`}</span>
      </div>
      {chips.length ? (
        <div className="liq-chips" role="group" aria-label="Aktive Filter">
          {chips.map((chip) => (
            <span className="liq-chip" key={chip.art}>
              {chip.text}
              <button
                type="button"
                aria-label={`Filter entfernen: ${chip.text}`}
                onClick={() => onFilter({ ...filter, [chip.art]: LEERER_FILTER[chip.art] })}
              >
                <Ikone name="x" />
              </button>
            </span>
          ))}
          <button type="button" className="liq-chips-alle" onClick={() => onFilter(LEERER_FILTER)}>
            Alle Filter zurücksetzen
          </button>
        </div>
      ) : null}
      <div className="liq-tabelle-wrap">
        <table className="ov liq-tabelle" ref={tabelle}>
          <thead>
            <tr>
              {kopf(
                "bestellung",
                "Bestellung",
                <input
                  type="text"
                  className="spaltenfilter-suche"
                  placeholder="Bestellnummer suchen…"
                  aria-label="Bestellung durchsuchen"
                  value={filter.bestellung}
                  onChange={(e) => set("bestellung", e.target.value)}
                />,
                Boolean(filter.bestellung.trim()),
              )}
              {kopf(
                "lieferant",
                "Lieferant",
                <MehrfachauswahlFilter label="Lieferant" optionen={lieferantenOptionen} ausgewaehlt={filter.lieferant} onAendern={(w) => set("lieferant", w)} />,
                filter.lieferant.length > 0,
              )}
              {kopf("rate", "Rate", null, false)}
              {kopf(
                "usd",
                "Betrag USD",
                <div className="liq-filter-betrag">
                  <BetragsBereichFilter
                    label="Betrag"
                    waehrungen={["USD"]}
                    bereiche={{ USD: filter.betrag }}
                    onAendern={(_waehrung, bereich: Bereichsfilter) => set("betrag", { von: bereich.von, bis: bereich.bis })}
                  />
                </div>,
                Boolean(filter.betrag.von || filter.betrag.bis),
                true,
              )}
              {kopf(
                "faellig",
                "Fällig",
                <DatumsBereichFilter label="Fällig" bereich={filter.faellig} onAendern={(b) => set("faellig", b)} />,
                Boolean(filter.faellig.von || filter.faellig.bis || filter.faellig.ohneWert),
              )}
              {kopf(
                "status",
                "Status",
                <MehrfachauswahlFilter
                  label="Status"
                  optionen={optionen([["fix", STATUS_LABEL.fix], ["geplant", STATUS_LABEL.geplant], ["offen", STATUS_LABEL.offen]])}
                  ausgewaehlt={filter.status}
                  onAendern={(w) => set("status", w)}
                />,
                filter.status.length > 0,
              )}
              {kopf("herkunft", "Herkunft", null, false)}
              {kopf(
                "stock",
                "Stock Order",
                <MehrfachauswahlFilter label="Stock Order" optionen={optionen([["ja", "Ja"], ["nein", "Nein"]])} ausgewaehlt={filter.stock} onAendern={(w) => set("stock", w)} />,
                filter.stock.length > 0,
              )}
              {kopf(
                "transport",
                "Transportart",
                <MehrfachauswahlFilter
                  label="Transportart"
                  optionen={optionen([["see", transportartLabel("see")], ["air", transportartLabel("air")], ["road", transportartLabel("road")]])}
                  ausgewaehlt={filter.transport}
                  onAendern={(w) => set("transport", w)}
                />,
                filter.transport.length > 0,
              )}
            </tr>
          </thead>
          <tbody>
            {zeilen.length === 0 ? (
              <tr>
                <td colSpan={9} className="liq-leer">
                  {alle.length === 0
                    ? "Keine offenen USD-Raten vorhanden."
                    : filterAktiv(filter)
                      ? "Keine Raten für die aktuelle Filterauswahl."
                      : "Keine Raten."}
                </td>
              </tr>
            ) : (
              zeilen.map((r) => (
                <tr key={r.schluessel} className={r.ueberfaellig ? "liq-ueberfaellig" : undefined}>
                  <td>
                    <Verweis
                      className="tieflink-anker"
                      replace
                      onClick={(e) => { if (istEinfacherKlick(e)) onOeffnen(r.orderId, "pay"); }}
                      ziel={{ ...ansicht, bestellung: { kennung: kennungVon(r.order, r.orderId), tab: "pay", aufgabe: null, dokument: null } }}
                    >
                      {r.orderNo || "Bestellung"}
                    </Verweis>
                  </td>
                  <td>{r.lieferant}</td>
                  <td>{r.label}</td>
                  <td className="num">{fmtUsd(r.usd)}</td>
                  <td className="liq-faellig">
                    {r.datum ? (
                      <>
                        {fmtDate(r.datum)}
                        <span className="liq-kw">{`KW ${String(r.kw?.week).padStart(2, "0")}`}</span>
                      </>
                    ) : (
                      "–"
                    )}
                  </td>
                  <td>
                    <span className={`liq-pille ${r.klasse}`}>{STATUS_LABEL[r.klasse]}</span>
                    {r.ueberfaellig ? <span className="liq-pille ueberfaellig">Überfällig</span> : null}
                  </td>
                  <td className="liq-herkunft">{r.herkunft}</td>
                  <td>{r.stock ? "Ja" : "—"}</td>
                  <td>{transportartLabel(r.transportMode)}</td>
                </tr>
              ))
            )}
          </tbody>
          {zeilen.length ? (
            <tfoot>
              <tr>
                <td colSpan={3}>{`${gefiltert.length} ${gefiltert.length === 1 ? "Rate" : "Raten"}`}</td>
                <td className="num">{fmtUsd(summe)}</td>
                <td colSpan={5}></td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
